import { useRef } from 'react'
import { useId, useSignal, useSignalEffect } from '@cascivo/core'
import type { Signal } from '@cascivo/core'

export interface UsePopoverOptions {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  placement?: 'top' | 'bottom' | 'left' | 'right'
  offset?: number
}

export interface UsePopoverReturn {
  triggerRef: React.RefObject<HTMLElement | null>
  popoverRef: React.RefObject<HTMLElement | null>
  isOpen: Signal<boolean>
  anchorName: string
  open: () => void
  close: () => void
  toggle: () => void
}

const supportsAnchor =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('anchor-name: --cascivo-a')

export function usePopover(options: UsePopoverOptions = {}): UsePopoverReturn {
  // `useId`, not a module counter: the anchor name is server-rendered into a style
  // property, so a counter that keeps incrementing for the server process's lifetime
  // diverges from the client's fresh counter and breaks hydration.
  const anchorName = `--${useId('cascivo-popover')}`
  const triggerRef = useRef<HTMLElement>(null)
  const popoverRef = useRef<HTMLElement>(null)
  const isOpen = useSignal(options.open ?? false)

  // Sync controlled prop into signal during render
  if (options.open !== undefined) {
    isOpen.value = options.open
  }

  const onOpenChangeRef = useRef(options.onOpenChange)
  onOpenChangeRef.current = options.onOpenChange

  const placementRef = useRef(options.placement)
  placementRef.current = options.placement

  const offsetRef = useRef(options.offset)
  offsetRef.current = options.offset

  useSignalEffect(() => {
    const open = isOpen.value
    onOpenChangeRef.current?.(open)

    const popover = popoverRef.current
    if (!popover) return

    if (open) {
      try {
        popover.showPopover()
      } catch {
        // Already shown or browser doesn't support Popover API
      }
    } else {
      try {
        popover.hidePopover()
      } catch {
        // Already hidden
      }
    }

    // Fallback positioning when CSS Anchor Positioning is unsupported
    if (!supportsAnchor && open) {
      const trigger = triggerRef.current
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      const placement = placementRef.current ?? 'bottom'
      const offset = offsetRef.current ?? 4
      if (placement === 'bottom') {
        popover.style.top = `${rect.bottom + offset + window.scrollY}px`
        popover.style.left = `${rect.left + window.scrollX}px`
      } else if (placement === 'top') {
        popover.style.top = `${rect.top - offset + window.scrollY}px`
        popover.style.left = `${rect.left + window.scrollX}px`
        popover.style.transform = 'translateY(-100%)'
      } else if (placement === 'left') {
        popover.style.top = `${rect.top + window.scrollY}px`
        popover.style.left = `${rect.left - offset + window.scrollX}px`
        popover.style.transform = 'translateX(-100%)'
      } else if (placement === 'right') {
        popover.style.top = `${rect.top + window.scrollY}px`
        popover.style.left = `${rect.right + offset + window.scrollX}px`
      }
    }
  })

  // Listen for popover hide (light-dismiss clicks)
  useSignalEffect(() => {
    const popover = popoverRef.current
    if (!popover) return
    const handler = (e: Event) => {
      const toggleEvent = e as ToggleEvent
      if (toggleEvent.newState === 'closed') {
        isOpen.value = false
      }
    }
    popover.addEventListener('toggle', handler)
    return () => popover.removeEventListener('toggle', handler)
  })

  // Astro's `transition:persist` without `Element.moveBefore` re-inserts the island by moving
  // an ancestor. Removal hides an open popover without firing `toggle`, so the listener above
  // never hears it and `isOpen` keeps saying open. Watch only while open.
  useSignalEffect(() => {
    const open = isOpen.value
    const popover = popoverRef.current
    if (!open || !popover) return
    const observer = new MutationObserver((records) => {
      if (!popover.isConnected || !wasReinserted(records, popover) || isPopoverOpen(popover)) {
        return
      }
      try {
        popover.showPopover()
      } catch {
        // Browser doesn't support Popover API
      }
    })
    observer.observe(popover.ownerDocument, { childList: true, subtree: true })
    return () => observer.disconnect()
  })

  return {
    triggerRef,
    popoverRef,
    isOpen,
    anchorName,
    open: () => {
      isOpen.value = true
    },
    close: () => {
      isOpen.value = false
    },
    toggle: () => {
      isOpen.value = !isOpen.value
    },
  }
}

function wasReinserted(records: MutationRecord[], el: Element): boolean {
  for (const record of records) {
    // Indexed, not `for…of`: iterating a NodeList needs `DOM.Iterable`, which an adopter's
    // tsconfig may lack.
    const added = record.addedNodes
    for (let i = 0; i < added.length; i++) {
      if (added.item(i)?.contains(el)) return true
    }
  }
  return false
}

// A re-inserted popover is never open, so an engine without `:popover-open` re-shows it.
function isPopoverOpen(el: HTMLElement): boolean {
  try {
    return el.matches(':popover-open')
  } catch {
    return false
  }
}
